import { getSBApiClient, createSBApiClient, SearchFilters } from './sbapi/sbapi-api-client';
import { FeaturedContentTypeDto, OptixTrackSearchResultRequest } from './sbapi/sbapi-api-model';

import { Subscription } from './sportsbook-api-types';

import { createSBApiWebsocketClient, getSBApiWebsocketClient } from './sbapi/sbapi-api-websocket-client';
import {
	connectToSTOMP as connectToBettingServiceSTOMPClient,
	disconnectFromSTOMP as disconnectFromBettingServiceSTOMPClient,
	updateBettingSessionExpiration as updateBettingSessionExpirationInSTOMPClient
} from './betting-service/api';

export * as Model from './sbapi/sbapi-api-model';

const API_PATH = '/sportsbook-api';

type SetupParams = {
	franchiseCode: string;
	locale: string;
	localeIsoCode: string;
	onSBApiWebsocketConnected?: () => void;
	onSBApiWebsocketConnectionError?: (reconnectAttempt: number) => void;
};

export const setUpSportsbookApi = (params: SetupParams) => {
	const { franchiseCode, localeIsoCode, onSBApiWebsocketConnected, onSBApiWebsocketConnectionError } = params;
	createSBApiClient(API_PATH, franchiseCode, localeIsoCode);

	const websocketHost = window.location.host;

	const websocketUrl = `wss://${websocketHost}/sportsbook-api/websocket?franchiseCode=${franchiseCode}&locale=${localeIsoCode}`;
	createSBApiWebsocketClient(websocketUrl, {
		onConnected: onSBApiWebsocketConnected,
		onConnectionError: onSBApiWebsocketConnectionError
	});
};

export const connectToSBApiWebsocketClient = async () => getSBApiWebsocketClient().connect();
export const disconnectFromSBApiWebsocketClient = () => getSBApiWebsocketClient().disconnect();
export const reconnectToSBApiWebsocketClientAndKeepSubscriptions = () =>
	getSBApiWebsocketClient().reconnectAndKeepSubscriptions();

export {
	connectToBettingServiceSTOMPClient,
	disconnectFromBettingServiceSTOMPClient,
	updateBettingSessionExpirationInSTOMPClient
};

export const subscribeToEvents = (subscription: Subscription) =>
	getSBApiWebsocketClient().subscribeToEvents(subscription);

export const unsubscribeFromEvents = (ref: string) => getSBApiWebsocketClient().unsubscribeFromEvents(ref);

export const hasSubscription = (ref: string) => getSBApiWebsocketClient().hasSubscription(ref);

export const fetchLeague = async (leagueId: string) => getSBApiClient().fetchLeague(leagueId);

export const fetchLeagues = async (
	sportId?: string,
	leagueIds?: Array<string>,
	isLive?: boolean,
	isOutrightsOnlyLeague?: boolean,
	startingAfter?: string,
	startingBefore?: string,
	hasOutrights?: boolean
) =>
	getSBApiClient().fetchLeagues(
		sportId,
		leagueIds,
		isLive,
		isOutrightsOnlyLeague,
		startingAfter,
		startingBefore,
		hasOutrights
	);

export const fetchSports = async () => getSBApiClient().fetchSports();

export const fetchSport = async (sportId: string) => getSBApiClient().fetchSport(sportId);

export const fetchSportDaysWithEvents = async (sportId: string, fromDate: string, toDate: string, timeZone: string) =>
	getSBApiClient().fetchSportDaysWithEvents(sportId, fromDate, toDate, timeZone);

export const fetchSelections = async (selectionIds: string[], isLocaleFallbackEnabled = true) =>
	getSBApiClient().fetchSelections(selectionIds, isLocaleFallbackEnabled);

export const fetchSelectionsByReference = async (referenceIds: string[], operatorId: string | null) =>
	getSBApiClient().fetchSelectionsByReference(referenceIds, operatorId);

export const fetchPromotions = async () => getSBApiClient().fetchPromotionItems();

export const fetchFeaturedPreCanneds = async (sportId?: string, leagueId?: string) =>
	getSBApiClient().fetchFeaturedPreCannedItems(sportId, leagueId);

export const fetchBetRecommendations = async (key: string) => getSBApiClient().fetchBetRecommendations(key);

export const fetchSearchResult = async (query: string, filters: SearchFilters, isOptixSearchEnabled: boolean) =>
	getSBApiClient().fetchSearchResult(query, filters, isOptixSearchEnabled);

export const fetchOperatorConfigurations = async () => getSBApiClient().fetchOperatorConfigurations();

export const fetchFeaturedLeagues = async () => getSBApiClient().fetchFeaturedLeagues();

export const fetchFeaturedContent = async (types: FeaturedContentTypeDto[]) =>
	getSBApiClient().fetchFeaturedContent(types);

export const fetchBetGeniusStreamingData = async (
	fixtureId: string,
	streamId: string,
	deliveryType: string,
	deliveryId: string,
	device: string,
	region: string,
	anonymousSessionId: string
) =>
	getSBApiClient().fetchBetGeniusStreamingData(
		fixtureId,
		streamId,
		deliveryType,
		deliveryId,
		device,
		region,
		anonymousSessionId
	);

export const fetchBetradarStreamingData = async (streamId: string, streamType: string) =>
	getSBApiClient().fetchBetradarStreamingData(streamId, streamType);

export const fetchBetGeniusStreamingConfiguration = async () => getSBApiClient().fetchBetGeniusStreamingConfiguration();

export const fetchPreferredMarketTypesForSport = async (sportId: string) =>
	getSBApiClient().fetchPreferredMarketTypesForSport(sportId);
export const fetchDefaultMarketsForSports = async (sportIds?: string[]) =>
	getSBApiClient().fetchDefaultMarketsForSports(sportIds);

export const fetchMatchBets = async (sportId: string) => getSBApiClient().fetchMatchBets(sportId);
export const trackOptixSearchResult = async (searchResult: OptixTrackSearchResultRequest) =>
	getSBApiClient().trackOptixSearchResult(searchResult);

export const fetchActivateBonusTokens = async () => {
	try {
		const response = await fetch('/rest/sportsbook/bonus/tokens/activate', {
			method: 'GET'
		});

		return (await response.json()) as { result: string };
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(error);
		throw error;
	}
};

export const fetchQuickLinks = async () => getSBApiClient().fetchQuickLinks();

export const fetchMarketGroupsForEvent = async (eventId: string) => getSBApiClient().fetchMarketGroupsForEvent(eventId);
export const fetchLuckyBets = async (
	sportIds: Array<string>,
	count: string,
	minTotalOdds: string,
	maxTotalOdds: string,
	excludeEventIds?: string
) => getSBApiClient().fetchLuckyBets(sportIds, count, minTotalOdds, maxTotalOdds, excludeEventIds);

// Avoid propagating sbapi-client changes, they will replace their own singletons.
if (import.meta.webpackHot) {
	import.meta.webpackHot.accept(['./sbapi/sbapi-api-client.ts', './sbapi/sbapi-api-websocket-client.ts'], () => {});
}
